import { MathUtils, PerspectiveCamera, Vector3 } from 'three';

const right = new Vector3();
const up = new Vector3();
const move = new Vector3();

/**
 * Never zero, so a camera that has ended up on its own target can still be panned away from it.
 * A zero-radius view is broken in every other respect too, but this is one of the two ways out of it.
 */
const MIN_PAN_DISTANCE = 0.01;

/**
 * Slide a camera and its orbit target together, across the plane the target sits on.
 *
 * Shared by the preview simulator's two orbit cameras — Designer's room and JUTE's world-scene camera —
 * which sit in different packages and must agree exactly, or a pan would feel like a different control
 * depending on which kind of scene was on screen.
 *
 * `deltaRight` and `deltaUp` are fractions of the VISIBLE HEIGHT at the target, not world units, which
 * is what makes one rate suit every scene: half a frame is half a frame whether the camera is a hand's
 * width from a drinks can or four metres back from a room. It is also what makes a drag track the
 * pointer exactly, when the caller divides pixels by the frame's height.
 *
 * Moving BOTH is the whole point — the target is the pivot, so leaving it behind would turn a pan into
 * a slow orbit. The axes come from the camera's own quaternion rather than the world's, so panning up
 * moves up the screen even with the camera pitched over, and rather than from its matrix, which
 * `lookAt` leaves stale.
 */
export const panCameraAcrossView = (camera: PerspectiveCamera, target: Vector3, deltaRight: number, deltaUp: number): void => {
    const distance = Math.max(camera.position.distanceTo(target), MIN_PAN_DISTANCE);
    const visibleHeight = 2 * distance * Math.tan(MathUtils.degToRad(camera.fov * 0.5));

    right.set(1, 0, 0).applyQuaternion(camera.quaternion);
    up.set(0, 1, 0).applyQuaternion(camera.quaternion);

    move.copy(right).multiplyScalar(deltaRight * visibleHeight).addScaledVector(up, deltaUp * visibleHeight);
    camera.position.add(move);
    target.add(move);
};
